import { autoinject, bindable } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { Router } from 'aurelia-router';
import * as moment from 'moment';
import 'moment-timezone';
import numeral from 'numeral';
import { CardToken } from '../../models/card-token';
import { Account, Customer } from '../../models/customer';
import { Installment } from '../../models/installment';
import { TemplateData } from '../../models/template-data';
import { State } from '../../services/global-state';
import { PayrazrService } from '../../services/payrazr';
import { PortalTemplateTypes } from '../../services/template-service';
import { AchToken } from '../../models/ach-token';
import { computedFrom } from 'aurelia-binding';
import { CombinedInstallmentAccountItem } from '../../models/combined-installment-account-item';
import { PaymentFeeType } from '../../models/hpfApi';
import { MerchantConsumerPortalFeeSettings } from '../../models/merchant-consumer-portal-fee-settings';
import { ApplePaySettings, ApplePayUtil, DigitalWalletPaymentDetails, DigitalWalletPaymentPlanDetails, DigitalWalletType } from '../../models/digital-wallet';
import { PaymentPlanSettings } from '../../models/payment-plan-settings';
import { PaymentPlanType } from '../../models/paymentPlanType';

@autoinject
export class SetupInstallments {
	@bindable installment: Installment;
	discount: any;
	validationErrors: any;
	account: any;
	customer: Customer;
	state: string;
	portfolioSettings: any;
	installmentSetting: any;
	numberOfRetries: any;
	balanceRange: any;
	minFuture: any;
	maxFuture: any;
	calendarDisplayMode: any;
	selectedFrequency: any;
	payToday: any;
	allowServiceFeesOnEveryTransaction: boolean;
	paymentInformation: any;
	calculatedFeeAmount: number = 0;
	calculatedTotalAmount: number = 0;
	cardNumber: any;
	cardInfoKey: any;
	paymentSchedules: any;
	singleServiceFee: any;
	totalServiceFee: any;
	paymentDateSettings: any;
	paymentDate: any;
	transactionError: any;
	firstName: any;
	lastName: any;
	today: any;
	signedDate: any;
	confirmationTemplateData: TemplateData;
	isSubmitting: boolean = false;
	lockedFields: LockedFields;
	negotiatorActivitySessionId: string;
	dateIsValid: boolean;
	@bindable nextPaymentDate: string;
	totalBalanceAmount: number;
	accounts: Array<Account>;
	isDigitalWallet: boolean;
	merchantKey: number;
	merchantConsumerPortalFeeSettings: MerchantConsumerPortalFeeSettings = new MerchantConsumerPortalFeeSettings();
	paymentPlanSettings: PaymentPlanSettings = new PaymentPlanSettings();

	get isApplePay(): boolean {
		return this.isDigitalWallet && this.paymentInformation.digitalWalletSettings?.isApplePay();
	}

	constructor(private router: Router, private globalState: State, private payrazrService: PayrazrService, private i18n: I18N) {
		this.discount = 0;
		this.validationErrors = [];
		this.state = 'setup';
		this.minFuture = 1;
		this.maxFuture = 60;
		this.calendarDisplayMode = 0;
		this.today = moment.tz(this.globalState.business.businessTimezone).format('MM/DD/YYYY');
	}

	activate(params, routeData) {
		this.accounts = routeData.accounts;
		if (!this.accounts || this.accounts.length < 1) {
			this.goBack();
		}

		this.account = this.globalState.accounts.find((element, index, array) => {
			return element.id === params.id;
		});

		this.customer = this.globalState.customer;

		if (this.globalState.isConsumerPortalGuarantorBillingEnabled) {
			this.globalState.associatedCustomers.forEach(customer => {
				customer.accounts.forEach(account => {
					if (this.account.customerAccountKey === account.customerAccountKey) {
						this.customer = customer;
					}
				});
			});
		}

		this.installment = routeData.installment;
		this.totalBalanceAmount = this.accounts.reduce((sum, currentAccount) => { return sum + currentAccount.balance } , 0);
		let discount = this.totalBalanceAmount * (this.installment.discountPercent / 100);

		this.installment.totalAmount = (this.totalBalanceAmount - discount).toFixed(2);

		let integerTotal = Math.round(parseInt(this.installment.totalAmount, 10) * 100);
		let integerReminder = integerTotal % this.installment.number;
		let integerAverage = Math.floor(integerTotal / this.installment.number);
		let integerFirst = integerAverage + integerReminder;

		this.installment.firstPayment = integerFirst / 100;
		this.installment.paymentAfterFirst = integerAverage / 100;

		this.portfolioSettings = this.globalState.business.fetchPortfolioSettings(this.account.portfolioName).settings;
		this.installmentSetting = this.portfolioSettings.paymentTypes.installments;
		this.numberOfRetries = parseInt(this.installmentSetting.numberOfRetriesForCardDeclines, 10);

		this.paymentPlanSettings.accountId = this.account.id;
		this.paymentPlanSettings.paymentPlanType = PaymentPlanType.Installment;
		this.paymentPlanSettings.paymentFeeType = PaymentFeeType.PaymentPlan;

		const currentPortfolio = this.globalState.business.fetchPortfolioSettings(this.account.portfolioName);
		// map key and fee settings from merchant settings
		this.merchantKey = currentPortfolio.merchantSettings.merchantKey;
		this.merchantConsumerPortalFeeSettings.feesEnabled = currentPortfolio.merchantSettings.feeSettings.serviceFeeEnabled;
		this.merchantConsumerPortalFeeSettings.allowFeesOnPaymentPlans = currentPortfolio.merchantSettings.feeSettings.allowFeesOnPaymentPlans;
		this.merchantConsumerPortalFeeSettings.allowServiceFeesOnEveryTransaction = currentPortfolio.merchantSettings.feeSettings.allowServiceFeesOnEveryTransaction;

		// tslint:disable-next-line: forin
		for (let index in this.installmentSetting.balanceRanges) {
		let balanceRange = this.installmentSetting.balanceRanges[index];
			if (this.totalBalanceAmount >= balanceRange.min && this.totalBalanceAmount <= balanceRange.max) {
				this.balanceRange = balanceRange;
				break;
			}
		}

		if (routeData.overrides && routeData.overrides.selectedFrequency) {
			this.selectedFrequency = routeData.overrides.selectedFrequency;
			this.frequencyToDisplayMode();
		}
		else {
			this.maxFuture = this.installmentSetting.maxFirstInstallmentFutureDays; // set maxFuture regardless of override frequency
		}

		this.lockedFields = routeData.overrides ? routeData.overrides.lockedFields : null;
		this.negotiatorActivitySessionId = routeData.negotiatorActivitySessionId;
		routeData.negotiatorActivitySessionId = null;
	}

	selectedFrequencyChanged() {
		this.frequencyToDisplayMode();
	}

	payTodayChanged() {
		this.frequencyToDisplayMode();
	}

	frequencyToDisplayMode() {
		this.calendarDisplayMode = 0;
		this.maxFuture = this.installmentSetting.maxFirstInstallmentFutureDays;

		let indexofSelectedFrequency = parseInt(this.selectedFrequency, 10);
		if (indexofSelectedFrequency === 5) { this.midofEachMonthHandler(); }
		if (indexofSelectedFrequency === 6) { this.endOfMonthHandler(); }
	}

	midofEachMonthHandler() {
		this.calendarDisplayMode = 1;
		let newDate = new Date(moment.tz(this.globalState.business.businessTimezone).format('MM/DD/YYYY'));
		let theNumOfDate = newDate.getDate();
		let currentMidOfMonth = new Date(newDate.getFullYear(), newDate.getMonth(), 15);
		let nextMidOfMonth = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 15);

		let midOfMonth = theNumOfDate < 15 ? currentMidOfMonth : nextMidOfMonth;
		this.nextPaymentDate = moment(midOfMonth).format('MM/DD/YYYY');
	}

	endOfMonthHandler() {
		this.calendarDisplayMode = 2;
		let newDate = new Date(moment.tz(this.globalState.business.businessTimezone).format('MM/DD/YYYY'));
		let nextEndOfMonth = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0);

		this.nextPaymentDate = moment(nextEndOfMonth).format('MM/DD/YYYY');
	}

	isEvenPayments(installment) {
		if (parseFloat(installment.firstPayment) - parseFloat(installment.paymentAfterFirst) === 0) {
			return true;
		}
		return false;
	}

	translateFrequency(frequencyId: number) {
		switch (frequencyId) {
			case 0:
				return this.i18n.i18next.t('selectAValue', 'Please select a value');
			case 1:
				return this.i18n.i18next.t('weekly', 'Weekly');
			case 2:
				return this.i18n.i18next.t('every2Weeks', 'Every 2 weeks');
			case 3:
				return this.i18n.i18next.t('every28Days', 'Every 28 Days');
			case 4:
				return this.i18n.i18next.t('monthly', 'Monthly');
			case 5:
				return this.i18n.i18next.t('eachMonth15', '15th of each month');
			case 6:
				return this.i18n.i18next.t('endOfMonth', 'End of month');
			case 7:
				return this.i18n.i18next.t('every2Months', 'Every 2 months');
		}
	}

	@computedFrom('balanceRange')
	get availableFrequencyIds(): number[] {
		let opts: number[] = this.balanceRange.recurringPaymentsFrequencies.map(freq => freq.id);
		return [0, ...opts]; // prepend 'Please select a value' option
	}

	@computedFrom('availableFrequencyIds')
	get availableFrequencyDescriptions(): string[] {
		return this.availableFrequencyIds.map(id => this.translateFrequency(id));
	}

	next(event) {
		this.validationErrors = event.detail.errors;
		this.validate();

		if (this.validationErrors.length === 0) {

			this.installment.payToday = this.payToday;
			this.installment.nextPaymentDate = this.nextPaymentDate;
			this.installment.selectedFrequency = this.selectedFrequency;
			this.paymentPlanSettings.installment = this.installment;

			this.paymentInformation = event.detail;
			this.cardNumber = this.paymentInformation.card ? this.paymentInformation.card.cardNumber : null;
			this.cardInfoKey = this.paymentInformation.card && this.paymentInformation.card.selectedPaymentMethod ? this.paymentInformation.card.selectedPaymentMethod.cardInfoKey : null;

			let paymentMethodType = 'card';
			if (this.paymentInformation.check) {
				paymentMethodType = 'ach';
			}

			this.paymentInformationFromForm();

			this.computePlannedPayments(paymentMethodType)
				.then(result => {
					this.paymentInformation.installment.plannedPayments = result.plannedPayments;
					this.getConfirmationTemplateData();
					if (this.isDigitalWallet && (this.paymentInformation.digitalWalletSettings?.isApplePay() ?? false)) {
						this.setApplePaySettingsRecurringFields(result.plannedPayments);
					}
					this.showConfirmation();
				})
				.catch(error => {
					console.error(`Unable to communicate with the server. Please try again. ${JSON.stringify(error)}`);
				});
		}
	}

	computePlannedPayments(paymentMethodType: string): Promise<any> {
		return this.payrazrService.computePlannedPayments(
			this.account.id,
			this.installment.totalAmount,
			this.installment.number,
			this.installment.nextPaymentDate,
			this.installment.payToday,
			this.installment.selectedFrequency,
			this.paymentInformation.zipCode,
			paymentMethodType,
			this.cardNumber,
			this.cardInfoKey,
			this.isDigitalWallet);
	}

	getPlanEndDate(plannedPayments: any): Date {
		if (!this.globalState.features.enableApplePay
			|| !plannedPayments
			|| plannedPayments.length < 1) {
			return null;
		}

		const billDates: number[] = plannedPayments.map((pp) => new Date(pp.billDate).getTime());
		return new Date(Math.max(...billDates));
	}

	generatePaymentScheduleByPlannedPayments(plannedPayments) {
		let paymentSchedules = [];
		this.singleServiceFee = plannedPayments[0].plannedServiceFee;
		this.totalServiceFee = 0;
		plannedPayments.forEach((plannedPayment, index, list) => {
			paymentSchedules.push({
				paymentDate: moment(plannedPayment.billDate, 'YYYY-MM-DD').format('MM/DD/YYYY'),
				amount: plannedPayment.billAmount,
				serviceFee: plannedPayment.planedServiceFee
			});
			this.totalServiceFee += plannedPayment.planedServiceFee; // TODO: Fix this misspelling in the backend first
		});

		return paymentSchedules;
	}

	getConfirmationTemplateData() {
		this.paymentSchedules = this.generatePaymentScheduleByPlannedPayments(this.paymentInformation.installment.plannedPayments);
		let total = parseFloat(this.totalServiceFee) + parseFloat(this.paymentInformation.amount);
		let accountsIds = this.getAccountsIds();
		let uniqueOriginalLenderNames = Array.from(new Set(this.accounts.map(account => account.originalLenderName)));
		let uniqueRepaymentTypes = Array.from(new Set(this.accounts.map(account => account.repaymentType)));
		const merchantKey = this.globalState.business.getMerchantKeyAssociatedToPortfolioByPortfolioName(this.accounts[0].portfolioName);

		const installmentDetails = {
			numberOfPayments: this.installment.number,
			totalPaymentAmount: this.installment.totalAmount,
			SelectedPaymentDate: this.installment.nextPaymentDate,
			payToday: this.installment.payToday,
			frequency: this.installment.selectedFrequency,
			zipCode: this.paymentInformation.zipCode,
			paymentMethodType: this.paymentInformation.check ? 'ach' : 'card',
			cardNumber: this.cardNumber,
			cardInfoKey: this.cardInfoKey,
			computePlanWithoutFees: this.isDigitalWallet
		};

		let customObject: any = {
			sumOfInstallment: this.isDigitalWallet && this.allowFeesOnPaymentPlans ? 'Will Display in Apple Pay' : numeral(this.paymentInformation.amount).format('($0,0.00)'),
			accountNumberLabel : this.globalState.business.portalSettings.accountNumberLabel,
			customerAccountNumber: accountsIds,
			accountNumber: accountsIds, // backward compatibility
			customerAccountName: this.getCustomerAccountsNames(),
			balance : numeral(this.totalBalanceAmount).format('($0,0.00)'),
			discount: this.paymentInformation.discount,
			fee: this.isDigitalWallet && this.allowFeesOnPaymentPlans ? 'Will Display in Apple Pay' : numeral(this.totalServiceFee).format('($0,0.00)'),
			total : numeral(total).format('($0,0.00)'),
			merchantName : this.globalState.business.name,
			numberOfRetries: this.paymentInformation.installment.numberOfRetriesForCardDeclines,
			accountName: this.customer.name,
			zipCode: this.isDigitalWallet ? 'Will Display in Apple Pay' : this.paymentInformation.zipCode,
			paymentMethod: this.paymentMethod,
			customerAccountLocationName: this.getCustomerAccountsLocationsName(),
			customerAccountLocationReferenceId: this.getCustomerAccountsLocationsReferencesIds(),
			billingPhone: this.globalState.customer.mobilePhone,
			billingEmail: this.globalState.customer.email,
			originalLenderName: uniqueOriginalLenderNames.join(', '),
			repaymentType: uniqueRepaymentTypes.join(', '),
			installmentDetails: JSON.stringify(installmentDetails)
		};

		this.confirmationTemplateData = new TemplateData(customObject, merchantKey);
	}

	getAccountsIds() {
		return this.accounts.map(account => account.referenceId).join(', ');
	}

	getCustomerAccountsNames() {
		return this.accounts.map(account => account.name).join(', ');
	}

	getCustomerAccountsLocationsName() {
		return this.accounts.filter(x => x.bucket).map(account => account.bucket?.displayName).join(', ');
	}

	getCustomerAccountsLocationsReferencesIds() {
		return this.accounts.filter(x => x.bucket).map(account => account.bucket?.referenceId).join(', ');
	}

	get paymentMethod() {
		if (this.paymentInformation.isWallet) {
			if (this.paymentInformation.card && this.paymentInformation.card.selectedPaymentMethod) {
				return this.i18n.i18next.t('walletPaymentMethods', { defaultValue: `${this.paymentInformation.card.selectedPaymentMethod.cardType} ending ${this.paymentInformation.card.selectedPaymentMethod.last4ofCard}`, paymentMethodTypes: this.paymentInformation.card.selectedPaymentMethod.cardType, accountNumber: this.paymentInformation.card.selectedPaymentMethod.last4ofCard });
			}
			else if (this.paymentInformation.check && this.paymentInformation.check.selectedPaymentMethod) {
				return this.i18n.i18next.t('walletPaymentMethods', { defaultValue: `${this.paymentInformation.check.selectedPaymentMethod.achAccountType} ending ${this.paymentInformation.check.selectedPaymentMethod.last4ofAccount}`, paymentMethodTypes: this.paymentInformation.check.selectedPaymentMethod.achAccountType, accountNumber: this.paymentInformation.check.selectedPaymentMethod.last4ofAccount });
			}
		}
		else {
			if (this.paymentInformation.isCardPayment) {
				return this.paymentInformation.card.description;
			}
			else if (this.paymentInformation.isCheckPayment) {
				return this.paymentInformation.check.description;
			}
			else if (this.paymentInformation.isDigitalWallet) {
				return this.paymentInformation.digitalWalletSettings.getDigitalWalletName();
			}
		}
	}

	get allowFeesOnPaymentPlans(): boolean {
		return this.merchantConsumerPortalFeeSettings.feesEnabled && this.merchantConsumerPortalFeeSettings.allowFeesOnPaymentPlans;
	}

	get customerAccountKeys(): number[] {
		return this.accounts.map((account) => account.customerAccountKey);
	}

	setApplePaySettingsRecurringFields(plannedPayments: any[]): void {
		if (!plannedPayments
			|| plannedPayments.length === 0
			|| !this.paymentInformation.digitalWalletSettings?.isApplePay()) {
			return;
		}

		const applePaySettings: ApplePaySettings = this.paymentInformation.digitalWalletSettings as ApplePaySettings;

		const firstPlannedPayment: any = plannedPayments[0];
		// recurring requests are set to pending to display 'amount pending' text in modal
		applePaySettings.totalType = 'pending';
		applePaySettings.recurringAmount = firstPlannedPayment.billAmount?.toString() ?? '0';
		applePaySettings.recurringLabel = '';
		applePaySettings.paymentDescription = this.i18n.i18next.t('setUpInstallmentPlan', 'Set Up An Installment Plan');
		applePaySettings.paymentTiming = 'recurring';
		applePaySettings.recurringPaymentStartDate = new Date(firstPlannedPayment.billDate);
		applePaySettings.recurringPaymentEndDate = this.getPlanEndDate(plannedPayments);
		applePaySettings.recurringPaymentIntervalUnit = ApplePayUtil.getRecurringPaymentDateUnit(this.installment.selectedFrequency);
		applePaySettings.recurringPaymentIntervalCount = ApplePayUtil.getRecurringPaymentIntervalCount(this.installment.selectedFrequency);
		applePaySettings.billingAgreement = ApplePayUtil.getBillingAgreement(this.merchantConsumerPortalFeeSettings, this.i18n);
		applePaySettings.mangementURL = this.getViewInstallmentUrl();

		this.paymentInformation.digitalWalletSettings = applePaySettings;
	}

	getViewInstallmentUrl(): string {
		let urlSplit: string[] = window.location.href.split('/');
		urlSplit.pop();
		return urlSplit.join('/') + '/view';
	}

	paymentInformationFromForm() {
		/*collect installment details to paymentinformation*/
		this.paymentInformation.installment = {
			payToday: JSON.parse(this.installment.payToday),
			numberOfInstallment: this.installment.number,
			frequency: this.installment.selectedFrequency,
			nextPaymentDate: this.installment.nextPaymentDate,
			numberOfRetriesForCardDeclines: this.installmentSetting.numberOfRetriesForCardDeclines
		};
		this.paymentInformation.amount = this.installment.totalAmount;
		this.paymentInformation.discount = this.installment.discountPercent;
		this.paymentInformation.isInstallment = true;
	}

	validate() {
		if (this.selectedFrequency === 0) {
			this.validationErrors.push(this.i18n.i18next.t('selectFrequencyOfRecurringPayments', 'Please select the frequency of recurring payments'));
		}
		if (this.payToday === null || this.payToday === undefined) {
			this.validationErrors.push(this.i18n.i18next.t('scheduleFirstPaymentTodayOrNot', 'Please select whether to schedule your first payment today or not'));
		}
		this.validateNextPaymentDate();

		// WCAG compliance
		if (this.validationErrors) {
			const validationSummary = document.getElementsByTagName('validation-summary')[0] as HTMLElement;

			// wait for section to load before focusing
			setTimeout(() => {
				validationSummary?.focus();
			}, 200);
		}
	}

	validateNextPaymentDate() {
		this.dateIsValid = true;
		let nextPaymentDate = moment(this.nextPaymentDate, 'MM/DD/YYYY');
		let maxFuture = moment(moment.tz(this.globalState.business.businessTimezone).format('MM/DD/YYYY')).add(this.maxFuture, 'd').endOf('day');
		let minFuture = moment(moment.tz(this.globalState.business.businessTimezone).format('MM/DD/YYYY')).add(this.minFuture, 'd').startOf('day');
		if (!nextPaymentDate.isValid()) {
			this.validationErrors.push(this.i18n.i18next.t('invalidPaymentDate', 'Invalid payment date') + ':' + this.nextPaymentDate);
			this.dateIsValid = false;
		}
		if (nextPaymentDate < moment(moment.tz(this.globalState.business.businessTimezone).format('MM/DD/YYYY')).startOf('day')) {
			this.validationErrors.push(this.i18n.i18next.t('paymentDateCannotBeInPast', 'Payment date cannot be in the past'));
			this.dateIsValid = false;
		}
		if (nextPaymentDate < minFuture) {
			this.validationErrors.push(this.i18n.i18next.t('paymentDateCannotBeLessThan', { defaultValue: `Payment date cannot be less than ${this.minFuture} day${this.minFuture > 1 ? 's' : ''} from now`, future: this.minFuture, sAddition: this.minFuture > 1 ? 's' : '' }));
			this.dateIsValid = false;
		}
		if (nextPaymentDate > maxFuture) {
			this.validationErrors.push(this.i18n.i18next.t('paymentDateCannotBeMoreThan', { defaultValue: `Payment date cannot be more than ${this.maxFuture} day${this.maxFuture > 1 ? 's' : ''} from now`, future: this.maxFuture, sAddition: this.maxFuture > 1 ? 's' : '' }));
			this.dateIsValid = false;
		}
		if (this.calendarDisplayMode === 1 && nextPaymentDate.toDate().getDate() !== 15) {
			this.validationErrors.push(this.i18n.i18next.t('paymentDateShouldBeFifteenthOfMonth', 'Payment date should be 15th of the month'));
			this.dateIsValid = false;
		}
		if (this.calendarDisplayMode === 2 && !this.isLastDayOfMonth(nextPaymentDate.toDate())) {
			this.validationErrors.push(this.i18n.i18next.t('paymentDateShouldBeLastDayOfMonth', 'Payment date should be last day of the month'));
			this.dateIsValid = false;
		}
	}

	isLastDayOfMonth(date) {
		let month = date.getMonth();
		let newDate = date;
		newDate.setDate(date.getDate() + 1);
		if (newDate.getMonth() !== month) {
			return true;
		}
		return false;
	}

	submit($event: CustomEvent) {
		this.paymentDate = moment(moment.tz(this.globalState.business.businessTimezone).format('MM/DD/YYYY'));

		let saveToWallet = this.paymentInformation.addPaymentMethodToWalletIsSelected;

		if (saveToWallet
			&& this.globalState.isConsumerPortalGuarantorBillingEnabled) {
			// Call save to wallet for a specific account after transaction success.
			this.paymentInformation.addPaymentMethodToWalletIsSelected = false;
		}

		return this.callApiBasedOnPaymentMechanism($event)
			.then(result => {
				if (result.response.isSuccessful) {
					this.paymentInformation.planId = result.response.planId;
					this.paymentInformation.merchantKey = result.response.merchantKey;
					this.paymentInformation.portalTemplateType = this.getPortalTemplateTypeFromPaymentInformation();
					this.paymentInformation.savedPaymentMethodToWallet = result.response.savedPaymentMethodToWallet;
				}
				else {
					this.paymentInformation.portalTemplateType = PortalTemplateTypes.TransactionFailed;
					this.paymentInformation.savedPaymentMethodToWallet = result.response.savedPaymentMethodToWallet;
					this.paymentInformation.resultCode = result.response.resultCode;
					this.paymentInformation.resultMessage = result.response.message;
				}
			})
			.then(() => {
				if (!saveToWallet
					|| !this.globalState.isConsumerPortalGuarantorBillingEnabled
					|| this.isDigitalWallet) {
					return;
				}

				let customerId: string = this.globalState.customer.id;

				// Use the customer.id of the customer in context.
				if (this.globalState.isConsumerPortalGuarantorBillingEnabled) {
					this.globalState.associatedCustomers.forEach(customer => {
						customer.accounts.forEach(account => {
							if (this.account.customerAccountKey === account.customerAccountKey) {
								customerId = customer.id;
							}
						});
					});
				}

				if (!!this.paymentInformation.card) {
					let cardToken = new CardToken();
					cardToken.cardholderName = this.paymentInformation.name;
					cardToken.cardNumber = this.paymentInformation.card.cardNumber;
					cardToken.expirationDate = this.paymentInformation.card.expirationDate;
					cardToken.zipCode = this.paymentInformation.zipCode;

					return this.payrazrService.addCardPaymentMethod(cardToken, false, customerId);
				} else if (!!this.paymentInformation.check) {
					let achToken = new AchToken();
					achToken.accountName = this.paymentInformation.name;
					achToken.accountNumber = this.paymentInformation.check.accountNumber;
					achToken.aba = this.paymentInformation.check.transitNumber;
					achToken.accountType = this.paymentInformation.check.accountType;
					achToken.addressLine1 = this.paymentInformation.check.addressLine1;
					achToken.zipCode = this.paymentInformation.zipCode;

					return this.payrazrService.addAchPaymentMethod(achToken, false, customerId);
				}
			})
			.then(response => {
				this.paymentInformation.savedPaymentMethodToWallet = response && response.isSuccessful;

				const receiptRoute = this.router.routes.find(x => x.name === 'installments/installment-receipt');
				receiptRoute.paymentInformation = this.paymentInformation;

				this.isSubmitting = false;
				this.router.navigateToRoute(receiptRoute.name, { id: this.account.id });

				if (this.isApplePay) {
					const isSuccessful: boolean = true;
					ApplePayUtil.completeSession($event, isSuccessful);
				}
			})
			.catch(error => {
				if (this.isDigitalWallet) {
					const isSuccessful: boolean = false;
					ApplePayUtil.completeSession($event, isSuccessful);
				}
				this.isSubmitting = false;
				this.transactionError = error;
			});
	}

	getPortalTemplateTypeFromPaymentInformation(): PortalTemplateTypes {
		return this.isCardPaymentMethod()
			? PortalTemplateTypes.CreditCardInstallmentPlanReceipt
			: PortalTemplateTypes.CheckInstallmentPlanReceipt;
	}

	callApiBasedOnPaymentMechanism(submitEvent: CustomEvent) {
		if (this.isCardPaymentMethod()) {
			let digitalWalletPaymentDetails: DigitalWalletPaymentDetails;
			if (this.isDigitalWallet) {
				this.singleServiceFee = submitEvent.detail.calculatedServiceFee;
				digitalWalletPaymentDetails = this.getDigitalWalletPaymentDetailsFromSubmitEvent(submitEvent);
			}

			return this.payrazrService.createCardInstallmentPaymentPlan(
				this.account,
				this.paymentInformation.amount,
				this.paymentInformation.installment.numberOfInstallment,
				this.paymentInformation.installment.nextPaymentDate,
				this.paymentInformation.name,
				this.paymentInformation.addressLine1,
				this.paymentInformation.billingCity,
				this.paymentInformation.billingState,
				this.paymentInformation.billingPhone,
				this.paymentInformation.billingEmail,
				this.paymentInformation.zipCode,
				this.totalServiceFee,
				this.singleServiceFee,
				this.paymentInformation.transactionDescription,
				this.paymentInformation.isThirdPartyPayment,
				this.paymentInformation.card,
				this.paymentInformation.installment.numberOfRetriesForCardDeclines,
				this.paymentInformation.installment.payToday,
				this.paymentInformation.installment.frequency,
				this.firstName,
				this.lastName,
				this.today,
				this.paymentInformation.discount,
				submitEvent.detail.content,
				submitEvent.detail.hashedContent,
				this.paymentInformation.addPaymentMethodToWalletIsSelected,
				this.negotiatorActivitySessionId,
				this.getCombinedInstallmentAccounts(),
				this.isDigitalWallet,
				digitalWalletPaymentDetails
			);
		} else if (this.paymentInformation.check) {
			return this.payrazrService.createCheckInstallmentPaymentPlan(
				this.account,
				this.paymentInformation.amount,
				this.paymentInformation.installment.numberOfInstallment,
				this.paymentInformation.installment.nextPaymentDate,
				this.paymentInformation.name,
				this.paymentInformation.addressLine1,
				this.paymentInformation.billingCity,
				this.paymentInformation.billingState,
				this.paymentInformation.billingPhone,
				this.paymentInformation.billingEmail,
				this.paymentInformation.zipCode,
				this.totalServiceFee,
				this.singleServiceFee,
				this.paymentInformation.transactionDescription,
				this.paymentInformation.isThirdPartyPayment,
				this.paymentInformation.check,
				0,
				this.paymentInformation.installment.payToday,
				this.paymentInformation.installment.frequency,
				this.firstName,
				this.lastName,
				this.signedDate,
				this.paymentInformation.discount,
				submitEvent.detail.content,
				submitEvent.detail.hashedContent,
				this.paymentInformation.addPaymentMethodToWalletIsSelected,
				null,
				this.negotiatorActivitySessionId,
				this.getCombinedInstallmentAccounts()
			);
		}
	}

	isCardPaymentMethod(): boolean {
		return !!this.paymentInformation.card
			|| (this.globalState.features.enableApplePay && this.paymentInformation.digitalWalletSettings?.isCard());
	}

	private getDigitalWalletPaymentDetailsFromSubmitEvent(submitEvent: CustomEvent): DigitalWalletPaymentDetails {
		if (submitEvent === null) {
			return null;
		}

		let digitalWalletPaymentDetails: DigitalWalletPaymentDetails;
		if (submitEvent.detail.digitalWalletType === DigitalWalletType.ApplePay) {
			const applePayPayment: ApplePayJS.ApplePayPayment = submitEvent.detail.payment;
			const applePayPaymentToken = JSON.stringify(applePayPayment.token);
			digitalWalletPaymentDetails = {
				authenticationPaymentToken: btoa(applePayPaymentToken),
				digitalWalletType: DigitalWalletType.ApplePay,
				isDebitCard: applePayPayment.token.paymentMethod.type === 'debit',
				isCreditCard: applePayPayment.token.paymentMethod.type === 'credit',
				accountName: applePayPayment.billingContact?.givenName + ' ' + applePayPayment.billingContact?.familyName,
				zipCode: applePayPayment.billingContact?.postalCode ?? '',
			};
		}

		return digitalWalletPaymentDetails;
	}

	getCombinedInstallmentAccounts(): Array<CombinedInstallmentAccountItem> {
		if (!this.globalState.features.portalMultiAccountPaymentPlan
			|| this.accounts.length === 1) {
			return [];
		}

		let combinedAccounts = Array<CombinedInstallmentAccountItem>();

		this.accounts.forEach(account => {
			let discount = account.balance * (this.installment.discountPercent / 100);
			let totalAmountPerAccount = Number((account.balance - discount).toFixed(2)) / this.installment.number;
			let combinedInstallmentAccountItem = new CombinedInstallmentAccountItem();
			combinedInstallmentAccountItem.customerAccountId = account.id;
			combinedInstallmentAccountItem.amount = totalAmountPerAccount;
			combinedAccounts.push(combinedInstallmentAccountItem);
		});

		return combinedAccounts;
	}

	showConfirmation() {
		this.state = 'confirmation'; // due to how navigation is handled, title must be dynamically set to reflect what view is active (WCAG compliance)
		document.title = 'Installment Payment Confirmation | Dashboard | myPayrazr portal';
	}

	hideConfirmation() {
		this.state = 'setup';
		document.title = 'Setup Installment Payment Plan | Dashboard | myPayrazr portal';
	}

	goBack() {
		this.router.navigateToRoute('secure');
		this.validationErrors = [];
		return;
	}

	reset() {
		this.validationErrors = new Array();
	}
}

export class LockedFields {
	amount: boolean;
	frequency: boolean;
}
